import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faArrowRightLong,
  faFilePdf,
  faBolt,
  faChartPie,
  faScrewdriverWrench,
  faHandshake,
  faEnvelope,
  faCalendarDays,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { jsPDF } from "jspdf";

const round1 = (n) => Math.round(n * 10) / 10;
const round0 = (n) => Math.round(n);

function formatMoney(n, currency) {
  const val = Number.isFinite(n) ? n : 0;
  const cur = currency || "USD";
  const rounded = Math.round(val);
  return `${cur} ${rounded.toLocaleString()}`;
}

function addPdfHeader(doc, title, subtitle, meta) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(20);
  doc.text(title, 40, 50);
  doc.setFontSize(11);
  doc.text(subtitle, 40, 70);
  doc.setFontSize(10);
  const rightX = pageWidth - 40;
  const metaLines = meta.filter(Boolean);
  let y = 50;
  metaLines.forEach((m) => {
    doc.text(m, rightX, y, { align: "right" });
    y += 14;
  });
  doc.setLineWidth(0.5);
  doc.line(40, 86, pageWidth - 40, 86);
}

function pdfWriteBlock(doc, text, x, y, maxWidth, fontSize = 11, lineHeight = 16) {
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(text, maxWidth);
  let cy = y;
  lines.forEach((line) => {
    doc.text(line, x, cy);
    cy += lineHeight;
  });
  return cy;
}

function pdfEnsureSpace(doc, y, needed, top = 50, bottom = 50) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - bottom) {
    doc.addPage();
    return top;
  }
  return y;
}

function pdfSectionTitle(doc, title, x, y) {
  doc.setFontSize(14);
  doc.text(title, x, y);
  return y + 18;
}

export default function JobAutomationReport() {
  const [form, setForm] = useState({
    role: "",
    tasks: "",
    tools: "",
    hoursPerWeek: "",
    hourlyCost: "",
    currency: "USD",
    contactEmail: "",
  });

  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const runAnalysis = async (e) => {
    e.preventDefault();
    if (loading) return;
  
    setLoading(true);
    setAnalysis(null);
  
    try {
      const res = await fetch(`${process.env.REACT_APP_LLM_API_URL}/api/analyze-job`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          role: form.role,
          tasks: form.tasks,
          tools: form.tools,
          hoursPerWeek: form.hoursPerWeek,
          hourlyCost: form.hourlyCost,
          currency: form.currency,
          contactEmail: form.contactEmail,
        }),
      });
  
      if (!res.ok) {
        throw new Error("Backend error");
      }
  
      const data = await res.json();
      setAnalysis(data);
    } catch (err) {
      console.error(err);
      alert("Failed to analyze job");
    } finally {
      setLoading(false);
    }
  };
  

  const downloadPDF = () => {
    if (!safeAnalysis) return;
  
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxWidth = pageWidth - 80;
  
    const now = new Date();
    const stamp = now.toLocaleString();
  
    const s = safeAnalysis.summary;
    const savings = s.savings;
    const distribution = s.distribution || { High: 0, Medium: 0, Low: 0 };
  
    addPdfHeader(
      doc,
      "Job Automation Report",
      "Summary of tasks, automation potential, savings, and recommended automations",
      [
        s.role ? `Role: ${s.role}` : "Role: (not provided)",
        `Generated: ${stamp}`,
        `Tasks: ${s.tasksCount}`,
      ]
    );
  
    let y = 110;
  
    y = pdfEnsureSpace(doc, y, 140);
    y = pdfSectionTitle(doc, "1) Executive Summary", 40, y);
  
    const exec =
      `Based on the tasks provided, the estimated automation potential for this role is ${round0(s.overallAutomation)}%. ` +
      `This score reflects repeatability, tool readiness, decision complexity, and communication load. ` +
      `Below you will find a task-by-task breakdown, estimated time and cost savings, and a recommended automation plan.`;
  
    y = pdfWriteBlock(doc, exec, 40, y, maxWidth, 11, 16) + 6;
  
    y = pdfEnsureSpace(doc, y, 120);
    doc.setFontSize(12);
    doc.text("Key Drivers", 40, y);
    y += 16;
  
    doc.setFontSize(10);
    doc.text(`Repeatability: ${round0(s.drivers.repeatability)}%`, 50, y); y += 14;
    doc.text(`Decision Complexity: ${round0(s.drivers.decisionComplexity)}%`, 50, y); y += 14;
    doc.text(`Communication Load: ${round0(s.drivers.communicationLoad)}%`, 50, y); y += 14;
    doc.text(`Tool Readiness: ${round0(s.drivers.toolReadiness)}%`, 50, y); y += 18;
  
    y = pdfEnsureSpace(doc, y, 180);
    y = pdfSectionTitle(doc, "2) Overall Estimated Savings", 40, y);
  
    const currency = savings.currency || "USD";
  
    const hoursLine =
      savings.hoursPerWeek > 0
        ? `Input workload: ${round1(savings.hoursPerWeek)} hrs/week. Estimated hours saved: ${round1(savings.weeklyHoursSaved)} hrs/week.`
        : "Input workload: not provided. Add hours/week to calculate time savings.";
  
    y = pdfWriteBlock(doc, hoursLine, 40, y, maxWidth, 11, 16) + 6;
  
    const valueLine =
      savings.hourlyCost > 0 && savings.hoursPerWeek > 0
        ? `Estimated value saved: ${formatMoney(savings.weeklyValue, currency)}/week, ${formatMoney(savings.monthlyValue, currency)}/month, ${formatMoney(savings.yearlyValue, currency)}/year (based on your hourly cost).`
        : "Add an hourly cost to calculate monetary savings.";
  
    y = pdfWriteBlock(doc, valueLine, 40, y, maxWidth, 11, 16) + 10;
  
    y = pdfEnsureSpace(doc, y, 120);
    doc.setFontSize(12);
    doc.text("Automation Distribution", 40, y);
    y += 16;
  
    doc.setFontSize(10);
    doc.text(`High: ${distribution.High} tasks`, 50, y); y += 14;
    doc.text(`Medium: ${distribution.Medium} tasks`, 50, y); y += 14;
    doc.text(`Low: ${distribution.Low} tasks`, 50, y); y += 16;
  
    y = pdfEnsureSpace(doc, y, 260);
    y = pdfSectionTitle(doc, "3) Task Breakdown and Automation Potential", 40, y);
  
    const col1 = 40;
    const col2 = pageWidth - 160;
    const col3 = pageWidth - 90;
  
    doc.setFontSize(10);
    doc.text("Task", col1, y);
    doc.text("Automation", col2, y);
    doc.text("Band", col3, y);
    y += 10;
  
    doc.setLineWidth(0.5);
    doc.line(40, y, pageWidth - 40, y);
    y += 16;
  
    const rows = safeAnalysis.taskTable.slice(0, 18);
  
    for (const r of rows) {
      y = pdfEnsureSpace(doc, y, 56, 70, 50);
  
      const taskLines = doc.splitTextToSize(r.task, col2 - col1 - 12);
      taskLines.slice(0, 3).forEach((ln, i) => {
        doc.text(ln, col1, y + i * 12);
      });
  
      doc.text(`${r.automationPotential}%`, col2, y);
      doc.text(r.band, col3, y);
  
      y += Math.max(24, taskLines.slice(0, 3).length * 12) + 10;
      doc.setLineWidth(0.2);
      doc.line(40, y - 8, pageWidth - 40, y - 8);
    }
  
    y = pdfEnsureSpace(doc, y, 280);
    y = pdfSectionTitle(doc, "4) Recommended Automation Plan", 40, y);
  
    const plan = safeAnalysis.recommendedAutomation.plan;
  
    for (const p of plan) {
      y = pdfEnsureSpace(doc, y, 220, 70, 50);
  
      doc.setFontSize(12);
      doc.text(p.title, 40, y);
      y += 16;
  
      doc.setFontSize(10);
      y = pdfWriteBlock(doc, `Why: ${p.why}`, 50, y, maxWidth - 10, 10, 14) + 4;
  
      y = pdfWriteBlock(doc, "What:", 50, y, maxWidth - 10, 10, 14) + 2;
      Array.isArray(p.what) && p.what.forEach(w => {
        y = pdfEnsureSpace(doc, y, 30, 70, 50);
        y = pdfWriteBlock(doc, `• ${w}`, 60, y, maxWidth - 20, 10, 14);
      });
  
      y = pdfWriteBlock(doc, `Best for: ${p.bestFor}`, 50, y + 4, maxWidth - 10, 10, 14) + 10;
    }
  
    y = pdfEnsureSpace(doc, y, 220);
    y = pdfSectionTitle(doc, "5) Get in Touch", 40, y);
  
    const cta = safeAnalysis.callToAction;
  
    y = pdfWriteBlock(
      doc,
      "If you want this turned into real automations, the next step is a short discovery call where we pick the highest-impact tasks and map your tools and data.",
      40,
      y,
      maxWidth,
      11,
      16
    ) + 8;
  
    doc.setFontSize(11);
    doc.text(`Contact: ${cta.contactEmail || "hello@yourdomain.com"}`, 40, y);
    y += 18;
  
    doc.setFontSize(10);
    cta.nextSteps.forEach(ns => {
      y = pdfEnsureSpace(doc, y, 24);
      y = pdfWriteBlock(doc, `• ${ns}`, 50, y, maxWidth, 10, 14);
    });
  
    doc.save("job-automation-report.pdf");
  };
  

  const cardStat = (label, value, icon) => (
    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
      <div className="flex items-center gap-2 text-slate-600 text-sm">
        <FontAwesomeIcon icon={icon} />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-extrabold text-slate-900">{value}</div>
    </div>
  );

  const safeAnalysis = useMemo(() => {
    if (!analysis) return null;
  
    const taskTable = Array.isArray(analysis.taskTable) ? analysis.taskTable : [];
  
    const distribution = taskTable.reduce(
      (acc, t) => {
        acc[t.band] = (acc[t.band] || 0) + 1;
        return acc;
      },
      { High: 0, Medium: 0, Low: 0 }
    );
  
    const plan = analysis.recommendedAutomation.plan;
  
    return {
      ...analysis,
      taskTable,
      summary: {
        role: analysis.summary?.role || form.role,
        tasksCount: taskTable.length,
        overallAutomation: analysis.summary?.overallAutomation ?? 0,
        drivers: analysis.summary?.drivers || {
          repeatability: 0,
          decisionComplexity: 0,
          communicationLoad: 0,
          toolReadiness: 0,
        },
        distribution,
        savings: analysis.summary?.savings,
      },
      recommendedAutomation: {
        plan,
        toolSpecific: Array.isArray(analysis.recommendedAutomation?.toolSpecific)
          ? analysis.recommendedAutomation.toolSpecific
          : [],
        riskNotes: Array.isArray(analysis.recommendedAutomation?.riskNotes)
          ? analysis.recommendedAutomation.riskNotes
          : [],
      },
      callToAction: {
        contactEmail: analysis.callToAction?.contactEmail || form.contactEmail,
        calendly: analysis.callToAction?.calendly || "",
        nextSteps: Array.isArray(analysis.callToAction?.nextSteps)
          ? analysis.callToAction.nextSteps
          : [
              "Select top 3 automatable tasks",
              "Design workflows",
              "Implement and measure ROI",
            ],
      },
    };
  }, [analysis, form]);
  


  return (
    <>
      <Helmet>
        <title>Job Automation Report</title>
      </Helmet>

      <section
        className="relative overflow-hidden"
        style={{ backgroundImage: 'url("./assets/consul.png")', backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-primary opacity-80" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 text-white">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <FontAwesomeIcon icon={faRobot} />
              <span className="text-sm">Automation Readiness and Savings Report</span>
            </div>
            <h1 className="mt-5 text-4xl md:text-6xl font-extrabold leading-tight">
              Job Automation <span className="text-blue-200">Report</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-200">
              Turn daily tasks into a clear automation plan: task-by-task automation %, estimated time savings, and recommended workflows.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-10">
          <form onSubmit={runAnalysis} className="p-8 rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div className="text-xl font-extrabold text-slate-900">Provide role, tasks, and tools</div>
            </div>

            <div className="mt-6 grid gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Job Role</label>
                <input
                  name="role"
                  value={form.role}
                  onChange={onChange}
                  placeholder="e.g., Operations Coordinator"
                  required
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Daily/Weekly Tasks (one per line)</label>
                <textarea
                  name="tasks"
                  value={form.tasks}
                  onChange={onChange}
                  placeholder={`Examples:
- Respond to customer emails and follow-ups
- Update CRM stages and notes
- Prepare weekly performance report
- Upload invoices and reconcile payments`
}
                  required
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 h-44 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Tools You Use</label>
                <textarea
                  name="tools"
                  value={form.tools}
                  onChange={onChange}
                  placeholder="e.g., Gmail, Google Sheets, Slack, HubSpot CRM, Notion"
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 h-28 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Hours / Week</label>
                  <input
                    name="hoursPerWeek"
                    value={form.hoursPerWeek}
                    onChange={onChange}
                    placeholder="e.g., 40"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Hourly Cost</label>
                  <input
                    name="hourlyCost"
                    value={form.hourlyCost}
                    onChange={onChange}
                    placeholder="e.g., 20"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Currency</label>
                  <select
                    name="currency"
                    value={form.currency}
                    onChange={onChange}
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                    <option value="PKR">PKR</option>
                    <option value="SAR">SAR</option>
                    <option value="AED">AED</option>
                  </select>
                </div>
              </div>

                <div>
                  <label className="text-sm font-semibold text-slate-700">Contact Email</label>
                  <input
                    name="contactEmail"
                    value={form.contactEmail}
                    onChange={onChange}
                    placeholder="hello@yourdomain.com"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-slate-400"
                  />
                </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full px-6 py-4 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-60"
              >
                {loading ? "Analyzing…" : <>Generate Report <FontAwesomeIcon icon={faArrowRightLong} /></>}
              </button>

              <div className="text-xs text-slate-500 leading-relaxed">
                Tip: Add hours/week + hourly cost to unlock savings estimates. If you skip them, you still get automation % and recommendations.
              </div>
            </div>
          </form>

          <div className="p-8 rounded-3xl border border-slate-200 bg-white shadow-sm">
            {!safeAnalysis && (
              <div className="h-full flex flex-col items-center justify-center text-center text-slate-500">
                <FontAwesomeIcon icon={faRobot} className="text-6xl mb-4 text-blue-600" />
                <div className="text-lg font-semibold">Your report appears here</div>
                <div className="mt-2 text-sm max-w-md">
                  Enter your role and tasks to generate a structured report including task-by-task automation potential, estimated savings, and a recommended automation plan.
                </div>
              </div>
            )}

            {safeAnalysis && (
              <div className="space-y-10">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <div className="text-sm text-slate-500">Overall automation potential</div>
                    <div className="text-6xl font-extrabold text-secondary">{round0(safeAnalysis.summary.overallAutomation)}%</div>
                    <div className="mt-2 text-slate-600">
                      Tasks analyzed: <span className="font-semibold">{safeAnalysis.summary.tasksCount}</span>
                      {safeAnalysis.summary.role ? (
                        <> , Role: <span className="font-semibold">{safeAnalysis.summary.role}</span></>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <button
                      onClick={downloadPDF}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:opacity-95"
                    >
                      <FontAwesomeIcon icon={faFilePdf} />
                      Download PDF
                    </button>
                    <a
                      href={safeAnalysis.callToAction.calendly || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary text-white font-semibold hover:opacity-95"
                    >
                      <FontAwesomeIcon icon={faCalendarDays} />
                      Book a Call
                    </a>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-4">
                  {cardStat("Repeatability", `${round0(safeAnalysis.summary.drivers.repeatability)}%`, faBolt)}
                  {cardStat("Decision Complexity", `${round0(safeAnalysis.summary.drivers.decisionComplexity)}%`, faChartPie)}
                  {cardStat("Communication Load", `${round0(safeAnalysis.summary.drivers.communicationLoad)}%`, faHandshake)}
                  {cardStat("Tool Readiness", `${round0(safeAnalysis.summary.drivers.toolReadiness)}%`, faScrewdriverWrench)}
                </div>

                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2 font-extrabold text-slate-900">
                    <FontAwesomeIcon icon={faCircleCheck} className="text-green-600" />
                    Summary of tasks and their percentage of automation
                  </div>
                  <div className="mt-3 text-sm text-slate-600 leading-relaxed">
                    The percentages below represent estimated automation potential for each task. High does not mean “remove humans”, it means you can automate execution while keeping human approval where needed.
                  </div>
                  <div className="mt-4 grid md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <div className="text-sm text-slate-500">High</div>
                      <div className="text-2xl font-extrabold text-slate-900">{safeAnalysis.summary.distribution.High}</div>
                      <div className="text-xs text-slate-500">Mostly automatable</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <div className="text-sm text-slate-500">Medium</div>
                      <div className="text-2xl font-extrabold text-slate-900">{safeAnalysis.summary.distribution.Medium}</div>
                      <div className="text-xs text-slate-500">Partial automation</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <div className="text-sm text-slate-500">Low</div>
                      <div className="text-2xl font-extrabold text-slate-900">{safeAnalysis.summary.distribution.Low}</div>
                      <div className="text-xs text-slate-500">Keep core manual</div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="font-extrabold text-slate-900">Overall estimated savings</div>
                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                    Savings are estimated from your hours/week and hourly cost. If you did not provide them, fill them in and regenerate.
                  </div>

                  <div className="mt-4 grid md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-500">Hours saved / week</div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {safeAnalysis.summary.savings.hoursPerWeek > 0
                          ? `${round1(safeAnalysis.summary.savings.weeklyHoursSaved)}`
                          : "—"}
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-500">Value saved / month</div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {safeAnalysis.summary.savings.hoursPerWeek > 0 && safeAnalysis.summary.savings.hourlyCost > 0
                          ? formatMoney(safeAnalysis.summary.savings.monthlyValue, safeAnalysis.summary.savings.currency)
                          : "—"}
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-500">Value saved / year</div>
                      <div className="text-2xl font-extrabold text-slate-900">
                        {safeAnalysis.summary.savings.hoursPerWeek > 0 && safeAnalysis.summary.savings.hourlyCost > 0
                          ? formatMoney(safeAnalysis.summary.savings.yearlyValue, safeAnalysis.summary.savings.currency)
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="font-extrabold text-slate-900">Task with percentage of potential automation</div>
                  <div className="mt-3 space-y-3">
                    {safeAnalysis.taskTable.slice(0, 20).map((t, idx) => (
                      <div key={idx} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <div className="flex items-start justify-between gap-4">
                          <div className="font-semibold text-slate-900">{t.task}</div>
                          <div className="shrink-0 text-right">
                            <div className="text-lg font-extrabold text-secondary">{t.automationPotential}%</div>
                            <div className="text-xs text-slate-500">{t.band}</div>
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-slate-600">
                          Recommended automation: <span className="font-semibold">{t.recommendedAutomation}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          Estimated savings (relative): {t.estSavingsPct}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="font-extrabold text-slate-900">Recommended automation</div>
                  <div className="mt-3 space-y-4">
                    {safeAnalysis.recommendedAutomation.plan.map((p, idx) => (
                      <div key={idx} className="p-5 rounded-2xl bg-slate-50 border border-slate-200">
                        <div className="text-lg font-extrabold text-slate-900">{p.title}</div>
                        <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                          <span className="font-semibold">Why:</span> {p.why}
                        </div>
                        <div className="mt-3 text-sm text-slate-700 font-semibold">What to implement</div>
                        <div className="mt-2 space-y-2">
                          {p.what.map((w, i) => (
                            <div key={i} className="text-sm text-slate-600">• {w}</div>
                          ))}
                        </div>
                        <div className="mt-3 text-sm text-slate-600">
                          <span className="font-semibold">Best for:</span> {p.bestFor}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <div className="font-extrabold text-slate-900">Estimated Savings</div>
                    <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                      Your overall estimated savings depends on workload and hourly cost. If you provided them, the report already calculated weekly/monthly/yearly value.
                      Practical expectation: start with top 3 tasks, automate, measure for 2 weeks, then scale.
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="font-extrabold text-slate-900">Tool-specific opportunities</div>
                    <div className="mt-2 space-y-2">
                      {safeAnalysis.recommendedAutomation.toolSpecific.length ? (
                        safeAnalysis.recommendedAutomation.toolSpecific.map((t, i) => (
                          <div key={i} className="text-sm text-slate-600">• {t}</div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-600">• No specific tools detected. Add tools and regenerate.</div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="font-extrabold text-slate-900">Risks and guardrails</div>
                    <div className="mt-2 space-y-2">
                      {safeAnalysis.recommendedAutomation.riskNotes.map((r, i) => (
                        <div key={i} className="text-sm text-slate-600">• {r}</div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-slate-900 text-white">
                  <div className="mt-2 text-slate-200 text-lg font-semibold">
                    Want this converted into real automations (workflows, AI assistant, dashboards) with measurable ROI?
                  </div>

                  <div className="mt-5 flex items-center justify-center">
                    
                  <button
  type="button"
  onClick={() => window.openCalendly?.()}
  className="inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-primary text-white font-semibold hover:opacity-95"
>
  <FontAwesomeIcon icon={faCalendarDays} />
  Book via Calendly
</button>
                  </div>

                  <div className="mt-6">
                    <div className="text-sm font-semibold text-white/90">Next steps</div>
                    <div className="mt-2 space-y-2">
                      {safeAnalysis.callToAction.nextSteps.map((ns, i) => (
                        <div key={i} className="text-sm text-slate-200">• {ns}</div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 text-xs text-slate-300">
                    PDF includes the same structure so it can be shared internally or sent to stakeholders.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
